import type { ComponentProps } from 'react'
import React, { useState } from 'react'
import TagManager from 'react-gtm-module'

import { AdminModeSwitch } from '@components/common/AdminModeSwitch'
import { UserDetails } from '@components/common/UserDetails'
import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { FeatureFlagDecorator } from '@components/utils/FeatureFlagDecorator'
import type { AppearanceVariant } from '@fourthwall/appearance'
import { useAppearance } from '@fourthwall/appearance'
import { useAuth } from '@fourthwall/auth'
import { Avatar, Dropdown, Icon } from '@fourthwall/components'
import { ArrowShortLeftIcon, ArrowShortRightIcon, TickIcon } from '@fourthwall/icons'
import { ShopSwitch } from '@modules/Dashboard/modules/ShopSwitch'
import { routing } from '@utils/routing'

import { useCurrentShop } from '../../hooks/useCurrentShop'
import * as S from './ProfileDropdown.styled'
import type { ConnectedShops } from './ProfileDropdown.types'

type ProfileDropdownProps = {
  variant?: 'avatar' | 'menu-item'
  label?: string
  avatarSize?: ComponentProps<typeof Avatar>['size']
  testid?: string
  backgroundColor?: string
  connectedShops: ConnectedShops | undefined
  partnerDashboardUrl: string | undefined
  user: { name: string; email: string } | undefined
}

export const ProfileDropdown = ({
  variant = 'avatar',
  label,
  avatarSize = 'medium',
  testid,
  backgroundColor,
  connectedShops,
  partnerDashboardUrl,
  user,
}: ProfileDropdownProps) => {
  const { logout } = useAuth()
  const { hasPermission } = useDashboardPermissions()
  const { appearance, setAppearance } = useAppearance()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { currentShop } = useCurrentShop()

  const [isOpen, setIsOpen] = useState(false)
  const [isAppearanceDropdownOpen, setIsAppearanceDropdownOpen] = useState(false)

  const handleSetAppearance = (newAppearance: AppearanceVariant) => {
    setAppearance(newAppearance)

    TagManager.dataLayer({
      dataLayer: {
        event: 'appearance_change',
        appearance_from: appearance,
        appearance_to: newAppearance,
        shopId: currentShop?.id || '',
      },
    })
  }

  const component = {
    'menu-item': (
      <S.MenuItem onClick={() => setIsOpen((state) => !state)}>
        <S.MenuItemContent>
          <S.MenuItemAvatarContainer>
            <S.AvatarWrapper>
              <Avatar name={user?.name || ''} size={avatarSize} />
              {label && (
                <S.AvatarLabel $borderColor={backgroundColor} $size={avatarSize}>
                  {label}
                </S.AvatarLabel>
              )}
            </S.AvatarWrapper>
          </S.MenuItemAvatarContainer>
          <S.MenuItemName>{user?.name || ''}</S.MenuItemName>
        </S.MenuItemContent>
        <Icon component={ArrowShortRightIcon} height={12} />
      </S.MenuItem>
    ),
    avatar: (
      <S.AvatarWrapper>
        <Avatar
          name={user?.name || ''}
          size={avatarSize}
          hoverable
          active={isOpen}
          onClick={() => setIsOpen((state) => !state)}
        />

        {label && (
          <S.AvatarLabel $borderColor={backgroundColor} $size={avatarSize}>
            {label}
          </S.AvatarLabel>
        )}
      </S.AvatarWrapper>
    ),
  }[variant]

  const getContent = () => {
    if (isAppearanceDropdownOpen) {
      return (
        <>
          <Dropdown.Item
            label="Back"
            leftIcon={ArrowShortLeftIcon}
            onClick={() => setIsAppearanceDropdownOpen(false)}
          />
          <div style={{ width: 300 }} />
          <Dropdown.Divider />
          <Dropdown.Heading label="Appearance" />
          <Dropdown.Item
            label="Use device mode"
            rightIcon={appearance === 'light dark' ? TickIcon : undefined}
            onClick={() => handleSetAppearance('light dark')}
          />
          <Dropdown.Item
            label="Dark mode"
            rightIcon={appearance === 'dark' ? TickIcon : undefined}
            onClick={() => handleSetAppearance('dark')}
          />
          <Dropdown.Item
            label="Light mode"
            rightIcon={appearance === 'light' ? TickIcon : undefined}
            onClick={() => handleSetAppearance('light')}
          />
        </>
      )
    }

    return (
      <>
        {user && <UserDetails name={user.name || 'My Account'} email={user.email} />}
        <S.AdminModeSwitchWrapper>
          <AdminModeSwitch />
        </S.AdminModeSwitchWrapper>

        <Dropdown.Divider />

        <ShopSwitch connectedShops={connectedShops} />

        {dashboardFeatureFlags.appearanceMode?.enabled && (
          <Dropdown.Item
            label="Appearance"
            rightIcon={ArrowShortRightIcon}
            onClick={() => setIsAppearanceDropdownOpen(true)}
          />
        )}

        {dashboardFeatureFlags.account?.enabled && hasPermission('accountSettings') && (
          <Dropdown.Item
            label={
              <FeatureFlagDecorator
                {...dashboardFeatureFlags.account}
                display="inline"
                spacing="small"
              >
                Account settings
              </FeatureFlagDecorator>
            }
            to={routing.account.self}
            onClick={() => setIsOpen(false)}
          />
        )}
        {partnerDashboardUrl && (
          <Dropdown.Item label="Partner dashboard" href={partnerDashboardUrl} target="_blank" />
        )}
        <Dropdown.Item
          label="Help center"
          href={routing.external.helpCenter.self}
          target="_blank"
          analytics="helpCenter"
        />
        <Dropdown.Item label="Logout" testid="logout" onClick={() => logout?.()} />
      </>
    )
  }

  return (
    <Dropdown
      content={getContent()}
      isOpen={isOpen}
      position="bottom-end"
      maxWidth={300}
      testid={testid}
      onClose={() => {
        setIsOpen(false)
        setIsAppearanceDropdownOpen(false)
      }}
    >
      {component}
    </Dropdown>
  )
}
